import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import type { UserProfile } from '../../types';

interface UserStats {
  profile: UserProfile;
  articleCount: number;
  deckCount: number;
  wordCount: number;
}

interface ArticleInfo {
  id: string;
  title: string;
  contentLength: number;
  tokenCount: number;
  savedAt: number;
}

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userArticles, setUserArticles] = useState<Record<string, ArticleInfo[]>>({});
  const [loadingArticles, setLoadingArticles] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/app', { replace: true });
      return;
    }
    loadUsers();
  }, [isAdmin, navigate]);

  async function loadUsers() {
    setLoading(true);
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const userStats: UserStats[] = [];

      for (const userDoc of usersSnap.docs) {
        const profile = { uid: userDoc.id, ...userDoc.data() } as UserProfile;

        // Get counts for each subcollection
        const [articlesSnap, decksSnap, wordsSnap] = await Promise.all([
          getDocs(collection(db, 'users', userDoc.id, 'articles')),
          getDocs(collection(db, 'users', userDoc.id, 'decks')),
          getDocs(collection(db, 'users', userDoc.id, 'words')),
        ]);

        userStats.push({
          profile,
          articleCount: articlesSnap.size,
          deckCount: decksSnap.size,
          wordCount: wordsSnap.size,
        });
      }

      userStats.sort((a, b) => (b.profile.createdAt || 0) - (a.profile.createdAt || 0));
      setUsers(userStats);
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadUserArticles(uid: string) {
    if (userArticles[uid]) {
      setExpandedUser(expandedUser === uid ? null : uid);
      return;
    }

    setLoadingArticles(uid);
    setExpandedUser(uid);
    try {
      const snap = await getDocs(collection(db, 'users', uid, 'articles'));
      const articles: ArticleInfo[] = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          title: data.title || 'Untitled',
          contentLength: (data.content || '').length,
          tokenCount: (data.tokens || []).length,
          savedAt: data.savedAt || 0,
        };
      });
      articles.sort((a, b) => b.savedAt - a.savedAt);
      setUserArticles(prev => ({ ...prev, [uid]: articles }));
    } catch (err) {
      console.error('Failed to load articles for', uid, err);
    } finally {
      setLoadingArticles(null);
    }
  }

  if (!isAdmin) return null;

  const totalArticles = users.reduce((sum, u) => sum + u.articleCount, 0);
  const totalWords = users.reduce((sum, u) => sum + u.wordCount, 0);
  const totalDecks = users.reduce((sum, u) => sum + u.deckCount, 0);

  return (
    <div className="p-4 pt-14 md:p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Admin Dashboard</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">User analytics and content overview</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{users.length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Total Users</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{totalArticles}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Total Articles</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{totalDecks}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Total Decks</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{totalWords}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Total Words Saved</div>
        </div>
      </div>

      {/* User list */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">Users</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No users found.</div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {users.map(({ profile, articleCount, deckCount, wordCount }) => (
              <div key={profile.uid}>
                <div className="px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                          {profile.email}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          profile.role === 'admin'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                            : 'bg-primary-100 text-primary-700 dark:bg-primary-500/20 dark:text-primary-400'
                        }`}>
                          {profile.role === 'admin' ? 'Admin' : profile.subscriptionTier}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300">
                          {profile.subscriptionStatus}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400">
                        Joined {new Date(profile.createdAt).toLocaleDateString()} · UID: {profile.uid.slice(0, 8)}...
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                      <span>{articleCount} articles</span>
                      <span>{deckCount} decks</span>
                      <span>{wordCount} words</span>
                      <button
                        onClick={() => loadUserArticles(profile.uid)}
                        className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-md text-xs font-medium transition-colors"
                      >
                        {expandedUser === profile.uid ? 'Hide' : 'View'} Articles
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded articles list */}
                {expandedUser === profile.uid && (
                  <div className="px-5 pb-4 bg-slate-50 dark:bg-slate-800/50">
                    {loadingArticles === profile.uid ? (
                      <div className="py-4 text-center text-sm text-slate-400">Loading articles...</div>
                    ) : (userArticles[profile.uid] || []).length === 0 ? (
                      <div className="py-4 text-center text-sm text-slate-400">No articles.</div>
                    ) : (
                      <div className="space-y-2 pt-2">
                        {(userArticles[profile.uid] || []).map(article => (
                          <div
                            key={article.id}
                            className="bg-white dark:bg-slate-700 rounded-lg p-3 border border-slate-200 dark:border-slate-600"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                                  {article.title}
                                </div>
                                <div className="text-xs text-slate-400 mt-1">
                                  {article.contentLength.toLocaleString()} chars · {article.tokenCount.toLocaleString()} tokens · {new Date(article.savedAt).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
